import { useState, useEffect, useRef } from "react";
import { useT } from "../../i18n/LanguageContext";

export default function PortfolioCarousel({
    images,
    alt,
    href,
    testid,
    kind = "photo", // "photo" | "screenshot"
}) {
    const { t } = useT();
    const [i, setI] = useState(0);
    const [paused, setPaused] = useState(false);
    const touchStartX = useRef(null);
    const n = images.length;
    const isShot = kind === "screenshot";
    const hasMany = n > 1;

    useEffect(() => {
        if (paused || !hasMany) return;
        const t = setInterval(() => setI((p) => (p + 1) % n), 6000);
        return () => clearInterval(t);
    }, [paused, n, hasMany]);

    const go = (next) => setI((p) => (p + next + n) % n);

    return (
        <div
            data-testid={testid}
            className={`relative overflow-hidden group ${
                isShot ? "bg-ink/5 ring-1 ring-warm/60" : ""
            }`}
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
            onTouchStart={(e) => (touchStartX.current = e.touches[0].clientX)}
            onTouchEnd={(e) => {
                if (!hasMany || touchStartX.current == null) return;
                const dx = e.changedTouches[0].clientX - touchStartX.current;
                if (Math.abs(dx) > 40) go(dx < 0 ? 1 : -1);
                touchStartX.current = null;
            }}
        >
            <div
                className={`flex transition-transform duration-[900ms] ease-[cubic-bezier(0.22,0.61,0.36,1)] ${
                    isShot ? "aspect-[16/9]" : "aspect-[4/3]"
                }`}
                style={{ transform: `translateX(-${i * 100}%)` }}
            >
                {images.map((src, idx) => {
                    // For local /portfolio/*.png|jpg assets we ship an AVIF (smallest, modern browsers),
                    // a WebP (widely supported, big savings) and the original raster (fallback).
                    // <picture> lets the browser pick the first format it understands.
                    const isLocalRaster = src.startsWith("/portfolio/") && /\.(png|jpg|jpeg)$/i.test(src);
                    const avifSrc = isLocalRaster ? src.replace(/\.(png|jpg|jpeg)$/i, ".avif") : null;
                    const webpSrc = isLocalRaster ? src.replace(/\.(png|jpg|jpeg)$/i, ".webp") : null;
                    const isFirst = idx === 0;
                    return (
                        <picture key={src} className="w-full h-full flex-shrink-0">
                            {avifSrc && <source srcSet={avifSrc} type="image/avif" />}
                            {webpSrc && <source srcSet={webpSrc} type="image/webp" />}
                            <img
                                src={src}
                                alt={`${alt} — ${idx + 1}`}
                                loading={isFirst ? "eager" : "lazy"}
                                decoding="async"
                                fetchPriority={isFirst ? "high" : "low"}
                                className={`w-full h-full flex-shrink-0 ${
                                    isShot ? "object-contain p-3 md:p-5" : "object-cover"
                                }`}
                            />
                        </picture>
                    );
                })}
            </div>

            {href ? (
                <a
                    href={href}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`${testid}-live-link`}
                    className="absolute top-4 left-4 bg-[var(--sand)] text-ink px-3 py-1 text-xs tracking-[0.16em] uppercase font-medium border border-ink/10 hover:bg-ink hover:text-[var(--sand)] transition-colors"
                >
                    {t("portfolio.liveBadge")} →
                </a>
            ) : (
                <div className="absolute top-4 left-4 bg-[var(--sand)] text-ink px-3 py-1 text-xs tracking-[0.16em] uppercase font-medium border border-ink/10">
                    {t("portfolio.privateBadge")}
                </div>
            )}

            {hasMany && (
                <>
                    <div className="absolute top-4 right-4 bg-[var(--sand)] text-ink px-2.5 py-1 text-[10px] tracking-[0.2em] font-medium border border-ink/10">
                        {String(i + 1).padStart(2, "0")} / {String(n).padStart(2, "0")}
                    </div>

                    <button
                        type="button"
                        aria-label="Previous image"
                        data-testid={`${testid}-prev`}
                        onClick={() => go(-1)}
                        className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center bg-[var(--sand)]/85 hover:bg-[var(--sand)] text-ink opacity-0 group-hover:opacity-100 transition-opacity duration-300 text-lg"
                    >
                        ‹
                    </button>
                    <button
                        type="button"
                        aria-label="Next image"
                        data-testid={`${testid}-next`}
                        onClick={() => go(1)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center bg-[var(--sand)]/85 hover:bg-[var(--sand)] text-ink opacity-0 group-hover:opacity-100 transition-opacity duration-300 text-lg"
                    >
                        ›
                    </button>

                    <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                        {images.map((_, idx) => (
                            <button
                                key={idx}
                                type="button"
                                aria-label={`Go to image ${idx + 1}`}
                                data-testid={`${testid}-dot-${idx}`}
                                onClick={() => setI(idx)}
                                className={`h-1.5 transition-all duration-500 ${
                                    idx === i
                                        ? `w-8 ${isShot ? "bg-ink" : "bg-[var(--sand)]"}`
                                        : `w-4 ${isShot ? "bg-ink/30 hover:bg-ink/60" : "bg-[var(--sand)]/50 hover:bg-[var(--sand)]/80"}`
                                }`}
                            />
                        ))}
                    </div>
                </>
            )}
        </div>
    );
}
